import Cookies from "js-cookie";
import { useCallback, useEffect, useMemo, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import { useLocation } from "react-router-dom";

import NewGeneralAPI from "src/api/new-interfaces/live/general";
import { TEST_MODE } from "src/consts/general";
import { useSelector, useState } from "src/hooks/state";
import { useUserInfo } from "src/hooks/user";
import useGateway from "src/hooks/gateway";
import { useNexus } from "src/hooks/useNexus";
import { KEY } from "src/consts/nexus-keys";
import { NON_ELIGILBE_BUSINESS_FORM, NON_ELIGILBE_INDUSTRIES } from "src/scenes/Guest/consts/users";
import useTrialInfo from "src/scenes/Home/hooks/useTrialInfo";
import { getMenus, getOnlySandbox, getOnlyVerification } from "../utils/menus";
import { chatWidgetAuth, closeChatWidget, openChatWidget } from "@help-center/flip-chat-widget";
import { loadToken } from "src/helpers/auth";
import { userVerifiedGuard } from "src/helpers/role";
import { useIsMobileWidth } from "src/hooks/window";
import { isLowMedRisk } from "src/scenes/NewVerification/utils/verificationUtils";
import { fetchBeneficiaryBanks } from "src/services/actions/bank";
import { fetchTrial } from "src/services/actions/trial";
import {
  fetchCompanyInfo,
  fetchUser,
  fetchUserRegisteredCompanies,
  fetchVerificationInfo,
  fetchVerifications
} from "src/services/actions/user";

const KEY_BFSB_COLLAPSED = "bfsb-collapsed";

const INITIAL_STATE = {
  showTestMode: false,
  showCompanyIdsModal: false,
  showAddBusinessModal: false,
  companyIds: []
};

window.ffbState = {
  isAuthChatWidget: false
};

export default function useAppLayout(props) {
  const location = useLocation();
  const { i18n } = useTranslation("translation");
  const dispatch = useDispatch();
  const { isNexusReady } = useGateway();

  const [state, setState] = useState({
    ...INITIAL_STATE,
    collapsed: Cookies.get(KEY_BFSB_COLLAPSED) === "true",
    userVerifInfo: {},
    verifications: {}
  });
  const { hasConfigTrial, isOldTrial } = useTrialInfo();
  // Nexus gate for the "Riwayat Refund" sidebar entry. getNexusPayload falls back to
  // DEFAULT_NEXUS_DATA, so this is false until Nexus says otherwise.
  const { getNexusPayload } = useNexus();
  const enableRefundMenu = getNexusPayload(KEY.PAY_REFUND_FLAGS).enabled;
  const isMobileWidth = useIsMobileWidth(769);

  const { beneficiaryBanks, loadingBeneficiaryBanks } = useSelector((state) => state.commonBank);
  const {
    loadingUser,
    user,
    loadingVerification,
    verificationInfo,
    verifications,
    loadingRegisteredCompanies,
    registeredCompanies,
    isOwner
  } = useSelector((state) => state.commonUser);

  const hasAction = new URLSearchParams(useLocation().search).get("actions");
  const isInDemo = new URLSearchParams(useLocation().search).get("demo");
  const hasUser = user && Object.entries(user).length > 0;
  const {
    isOnTrial,
    isKYCDone,
    isKYCApproved,
    isUserVerified,
    kycStatus,
    companyFormOfBusiness,
    companyIndustry,
    isUMKM,
    isLowMedIndustry,
    isKYCRequested,
    isKYCNegotiate
  } = useUserInfo();

  const { collapsed, showTestMode } = state;
  const { autoCollapsed, autoExpand } = props;

  const PAGE_CONTENT_REF = useRef(null);
  const MENUS = useMemo(() => {
    if (hasUser) {
      if (showTestMode) {
        return getOnlySandbox(user, true);
      } else if ((isOwner && isUserVerified) || (!isOwner && user?.current_user?.email_verified) || isKYCDone) {
        return getMenus(user, kycStatus, true, enableRefundMenu);
      }
      return getOnlyVerification();
    }
    return [];
  }, [hasUser, user, showTestMode, isOwner, i18n.language, kycStatus, enableRefundMenu]);

  useEffect(() => {
    if (registeredCompanies?.companies?.length > 0) {
      setState({ companyIds: registeredCompanies.companies });
    }
  }, [registeredCompanies]);

  useEffect(() => {
    if (hasUser) {
      const token = loadToken();
      dispatch(fetchUserRegisteredCompanies(token));
    }
  }, [hasUser]);

  const handleToggleClick = useCallback(
    (value) => {
      const nextState = typeof value === "boolean" ? value : !collapsed;
      setState({ collapsed: nextState });
      Cookies.set(KEY_BFSB_COLLAPSED, nextState);
    },
    [collapsed]
  );

  function checkMode() {
    const mode = location.pathname.split("/")[1];
    if (mode === TEST_MODE.SANDBOX) {
      setState({ showTestMode: true });
    } else {
      setState({ showTestMode: false });
    }
  }

  /* Check current status
   *
   * How?
   * Hit any endpoint (eg: operational hours),
   * - if it returns value, then the status is OK (no maintenance)
   * - else, maintenance
   *
   */
  async function checkCurrentStatus() {
    try {
      await NewGeneralAPI.getOperationalHours();
    } catch (errors) {}
  }

  useEffect(() => {
    checkMode();

    if (autoCollapsed) {
      setState({ collapsed: true });
      Cookies.set(KEY_BFSB_COLLAPSED, true);
    }

    if (autoExpand && !isMobileWidth) {
      setState({ collapsed: false });
      Cookies.set(KEY_BFSB_COLLAPSED, false);
    }

    if (!hasUser && !loadingUser && !!loadToken()) {
      dispatch(fetchUser());
      dispatch(fetchCompanyInfo());
      if (isNexusReady) {
        dispatch(fetchTrial());
      }
    }
  }, [user, isOnTrial, isNexusReady]);

  useEffect(() => {
    if (hasUser && user?.company?.kyc_status !== 2 && isOwner) {
      dispatch(fetchVerificationInfo());
      if (isNexusReady) {
        dispatch(fetchVerifications());
      }
    }

    const isAuthChatWidget = window.ffbState.isAuthChatWidget;

    if (hasUser && user?.company?.yellow_token && !isAuthChatWidget) {
      closeChatWidget();
      chatWidgetAuth({
        ymAuthenticationToken: user?.company?.yellow_token,
        payload: {
          //id
          company_id: user?.company?.id.toString(),
          // company id
          ffb_id: user?.company?.company_id,
          // company email
          user_email: `${user?.current_user?.email}`,
          // company name
          company_name: user?.company?.name_of_business
        }
      });
      if (hasAction === "open-chat") {
        openChatWidget();
      }
      window.ffbState.isAuthChatWidget = true;
    }
  }, [user, isOwner, isNexusReady]);

  useEffect(() => {
    if (verificationInfo && Object.keys(verificationInfo).length > 0) {
      setState({ userVerifInfo: verificationInfo });
    }
    if (verifications && Object.keys(verifications).length > 0) {
      setState({ verifications: verifications });
    }
  }, [verificationInfo, verifications]);

  useEffect(() => {
    checkCurrentStatus();
    const noBeneficiaryBanks = !beneficiaryBanks || Object.entries(beneficiaryBanks).length === 0;
    if (noBeneficiaryBanks && !loadingBeneficiaryBanks) {
      dispatch(fetchBeneficiaryBanks());
    }
  }, []);

  useEffect(() => {
    userVerifiedGuard(user, isOwner);
  }, [user, isOwner]);

  return {
    state,
    methods: {
      handleToggleClick,
      handleShowCompanyIdsModal: () => setState({ showCompanyIdsModal: true }),
      handleCloseCompanyIdsModal: () => setState({ showCompanyIdsModal: false }),
      handleShowAddBusinessModal: () => setState({ showAddBusinessModal: true }),
      handleCloseAddBusinessModal: () => setState({ showAddBusinessModal: false })
    },
    consts: {
      MENUS,
      PAGE_CONTENT_REF,
      DEPOSIT: showTestMode ? user.company_sandbox?.deposit : user.company?.deposit,
      USER: user,
      IS_SHOW_BANNER:
        isKYCDone ||
        ((isUMKM || isLowMedIndustry) && isKYCApproved) ||
        (isLowMedIndustry && (isKYCRequested || isKYCNegotiate)),
      IS_LOADING_USER: loadingUser,
      IS_LOADING_VERIFICATION: loadingVerification,
      IS_LOADING_REGISTERED_COMPANIES: loadingRegisteredCompanies,
      IS_VERIFICATION_PAGE: ["overview", "verification"].includes(location.pathname.split("/")[1]),
      IS_OWNER: isOwner,
      HAS_CONFIG_TRIAL: hasConfigTrial,
      NOT_ELIGIBLE_TRIAL:
        NON_ELIGILBE_BUSINESS_FORM.includes(user?.company?.form_of_business) ||
        NON_ELIGILBE_INDUSTRIES.includes(user?.company?.industry),
      IS_OLD_TRIAL: isOldTrial,
      IS_LOW_MED_RISK_COMPANY: isLowMedRisk(companyFormOfBusiness, companyIndustry),
      IS_DEMO: isInDemo
    }
  };
}
